import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { History } from "lucide-react";
import { api } from "@/lib/api-client";
import { todayIso } from "@/lib/format";
import { useAppStore } from "@/store/app-store";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import { StateView } from "@/components/ui/StateView";
import { BookingDetailDialog } from "@/components/resource/booking-detail";
import type { Booking } from "@/types/domain";
import { ActivityList, type ActivityEntry } from "./ActivityList";

type ActivityPage = { entries: ActivityEntry[]; hasMore: boolean; nextBefore: string | null; people: { id: string; name: string }[] };

const CATEGORIES = [
  { value: "", label: "All" },
  { value: "BOOKING", label: "Bookings" },
  { value: "STAY", label: "Check-in / stay" },
  { value: "MONEY", label: "Money" },
  { value: "GUESTS", label: "Guests" },
];

const daysAgo = (days: number) => todayIso(new Date(Date.now() - days * 86_400_000));

// Property-wide "who did what": every action, payment and refund with the person and time. Needs the Reports
// permission (main owner, admins, Full owner / Manager members).
export function ActivityPage() {
  const propertyId = useAppStore((state) => state.activeProperty!.id);
  const role = useAppStore((state) => state.user?.role);
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(todayIso());
  const [userId, setUserId] = useState("");
  const [category, setCategory] = useState("");
  const [openBooking, setOpenBooking] = useState<Booking | null>(null);
  const valid = Boolean(from && to && from <= to);
  const query = useInfiniteQuery({
    queryKey: ["bookings", propertyId, "activity", from, to, userId, category],
    initialPageParam: "",
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ propertyId, from, to });
      if (userId) params.set("userId", userId);
      if (category) params.set("category", category);
      if (pageParam) params.set("before", pageParam);
      return api<ActivityPage>(`/reports/activity?${params.toString()}`);
    },
    getNextPageParam: (last) => (last.hasMore && last.nextBefore ? last.nextBefore : undefined),
    enabled: valid,
  });
  const entries = query.data?.pages.flatMap((page) => page.entries) ?? [];
  const people = query.data?.pages[0]?.people ?? [];
  const openById = async (bookingId: string) => setOpenBooking(await api<Booking>(`/bookings/${bookingId}`));

  return (
    <main className="page activity-page">
      <header className="page-head resource-head">
        <div>
          <h1>Activity log</h1>
          <p className="page-head__meta">Who did what on this property's bookings</p>
        </div>
      </header>
      <section className="card activity-filters">
        <label>From<DateInput max={to} value={from} onChange={(event) => setFrom(event.target.value)} /></label>
        <label>To<DateInput min={from} max={todayIso()} value={to} onChange={(event) => setTo(event.target.value)} /></label>
        <label>Person
          <select value={userId} onChange={(event) => setUserId(event.target.value)}>
            <option value="">Everyone</option>
            {people.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
          </select>
        </label>
      </section>
      <div className="booking-filter-tabs">
        {CATEGORIES.map((option) => (
          <button key={option.value} type="button" className={option.value === category ? "filter-tab filter-tab--active" : "filter-tab"} onClick={() => setCategory(option.value)}>{option.label}</button>
        ))}
      </div>
      {!valid && <div className="form-error">Pick a valid date range.</div>}
      {query.isError && <StateView title="Couldn't load the activity" message={query.error.message} action={() => void query.refetch()} />}
      {query.isLoading && <div className="form-hint">Loading activity...</div>}
      {query.data && !entries.length && <div className="card"><div className="mini-empty"><History /><p>Nothing happened in this period{userId ? " by this person" : ""}.</p></div></div>}
      {entries.length > 0 && <section className="card"><ActivityList entries={entries} showBooking onOpenBooking={(id) => void openById(id)} /></section>}
      {query.hasNextPage && <Button variant="secondary" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>Show older</Button>}
      {openBooking && <BookingDetailDialog booking={openBooking} role={role === "ADMIN" ? "ADMIN" : "OWNER"} onClose={() => setOpenBooking(null)} />}
    </main>
  );
}
